import { useState, useEffect, useContext, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Divider,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  TextField,
  Autocomplete,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  OutlinedInput,
  ListItemText,
} from "@mui/material";

import { Replace, Trash2, PlusCircle } from "lucide-react";

import { useHttpClient } from "../../../../shared/hooks/http-hook";
import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import toast from "react-hot-toast";

import NotaInformativa from "../../../../shared/components/notaInformativa/NotaInformativa";

import "./ModalSubstituicaoOrcamento.scss";

const DENTES_CRIANCAS = [
  "51",
  "52",
  "53",
  "54",
  "55",
  "61",
  "62",
  "63",
  "64",
  "65",
  "71",
  "72",
  "73",
  "74",
  "75",
  "81",
  "82",
  "83",
  "84",
  "85",
  "Arcada Superior",
  "Arcada Inferior",
  "Arcadas",
];

const DENTES_ADULTOS = [
  "11",
  "12",
  "13",
  "14",
  "15",
  "16",
  "17",
  "18",
  "21",
  "22",
  "23",
  "24",
  "25",
  "26",
  "27",
  "28",
  "31",
  "32",
  "33",
  "34",
  "35",
  "36",
  "37",
  "38",
  "41",
  "42",
  "43",
  "44",
  "45",
  "46",
  "47",
  "48",
];

const DENTES = [...DENTES_ADULTOS, ...DENTES_CRIANCAS];
const FACES_OPCOES = ["D", "M", "O/I", "P/L", "V"];

function ModalSubstituicaoOrcamento({ open, onClose, orcamento }) {
  const { sendRequest } = useHttpClient();
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);

  // Dados do formulário
  const [manterTipo, setManterTipo] = useState(true);
  const [novoTipoCobertura, setNovoTipoCobertura] = useState("");
  const [instituicaoSelecionada, setInstituicaoSelecionada] = useState("");
  const [instituicoesDisponiveis, setInstituicoesDisponiveis] = useState([]);
  const [modalidadePagamento, setModalidadePagamento] = useState("DIRETO");
  const [desconto, setDesconto] = useState(0);
  const [tipoDesconto, setTipoDesconto] = useState("$");
  const [majoracao, setMajoracao] = useState(0);
  const [comMajoracao, setComMajoracao] = useState(false);
  const [observacoes, setObservacoes] = useState("");
  const [motivoSelecionado, setMotivoSelecionado] = useState("");
  const [motivoOutroTexto, setMotivoOutroTexto] = useState("");

  // Procedimentos
  const [procedimentos, setProcedimentos] = useState([]);
  const [novoIdProcedimento, setNovoIdProcedimento] = useState(null);
  const [tratamentosDisponiveis, setTratamentosDisponiveis] = useState([]);
  const [loadingTratamentos, setLoadingTratamentos] = useState(false);

  // Preview
  const [previewDados, setPreviewDados] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Alertas de validação
  const [alertasValidacao, setAlertasValidacao] = useState([]);

  const steps = [
    "Tipo de Orçamento",
    "Procedimentos",
    "Valores",
    "Confirmação",
  ];

  const buscarOrcamentoComPlanoPopulado = async () => {
    const orcamentoPopuladoPlano = await sendRequest(
      `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/planopopulado/${orcamento._id}`,
      "GET",
      null,
      { Authorization: "Bearer " + auth.token }
    );
    return orcamentoPopuladoPlano;
  };

  // Inicializar modal
  useEffect(() => {
    if (!open || !orcamento) return;

    const init = async () => {
      // Resetar estados
      setActiveStep(0);
      setManterTipo(true);
      setNovoTipoCobertura("");
      setInstituicaoSelecionada("");
      setModalidadePagamento("DIRETO");
      setDesconto(
        orcamento.desconto
          ? orcamento.tipoDesconto === "$"
            ? orcamento.desconto
            : orcamento.percentagemDescontoOriginal
          : 0
      );
      setTipoDesconto(orcamento.tipoDesconto || "$");
      setMajoracao(orcamento.majoracao || 0);
      setComMajoracao(orcamento.comMajoracao || false);
      setObservacoes(orcamento.observacoes || "");
      setMotivoSelecionado("");
      setMotivoOutroTexto("");
      setPreviewDados(null);
      setAlertasValidacao([]);

      // 1) Buscar orçamento com plano populado (AGUARDA)
      let orcamentoPopulado = null;
      try {
        const orcamentoPopuladoTemp = await buscarOrcamentoComPlanoPopulado();
        orcamentoPopulado = orcamentoPopuladoTemp.orcamento;
      } catch (e) {
        console.error("Erro ao buscar orçamento populado:", e);
        // se falhar, seguimos sem a info de executado (ou podes dar toast)
      }

      // 2) Montar procedimentos iniciais já com "executado"
      const procsIniciais = orcamento.procedimentos.map((proc) => {
        const tratamentoId = proc.tratamento?._id || proc.tratamento;
        const tratamentoObj =
          typeof proc.tratamento === "object" ? proc.tratamento : null;

        // lista de dentes
        let listaDentes = DENTES;
        if (tratamentoObj?.dentesEspecificos) {
          listaDentes = tratamentoObj.dentesEspecificos
            .split(",")
            .map((d) => d.trim());
        }

        // dentes como array
        let dentesArray = [];
        if (proc.dente) {
          dentesArray = proc.dente.includes(",")
            ? proc.dente.split(",").map((d) => d.trim())
            : [proc.dente.trim()];
        }

        // executado?
        let executado = false;
        const procsPlano =
          orcamentoPopulado?.planoTratamento?.procedimentos || [];

        executado = procsPlano.some((procPlano) => {
          const tratamentoMatch =
            (procPlano.tratamento?._id || procPlano.tratamento)?.toString() ===
            tratamentoId?.toString();

          const denteMatch = (procPlano.dente || "") === (proc.dente || "");

          const facesMatch =
            JSON.stringify((procPlano.faces || []).slice().sort()) ===
            JSON.stringify((proc.faces || []).slice().sort());

          return (
            tratamentoMatch &&
            denteMatch &&
            facesMatch &&
            (procPlano.sessoes?.length || 0) > 0
          );
        });

        return {
          id: proc.id,
          tratamento: tratamentoId,
          tratamentoObj,
          dentes: dentesArray,
          faces: proc.faces || [],
          listaDentes,
          executado,
        };
      });

      setProcedimentos(procsIniciais);

      // 3) Buscar listas auxiliares
      buscarTratamentos();
      buscarInstituicoes();
    };

    init();
  }, [open, orcamento]);

  useEffect(() => {
    const modalidadeFinal = manterTipo
      ? orcamento.modalidadePagamento
      : modalidadePagamento;

    // Forçar majoração = 0 se for reembolso cliente
    if (modalidadeFinal === "REEMBOLSO_CLIENTE") {
      setMajoracao(0);
      setComMajoracao(false);
    }
  }, [manterTipo, modalidadePagamento, orcamento.modalidadePagamento]);

  const buscarInstituicoes = async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/instituicoes/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token }
      );
      setInstituicoesDisponiveis(
        responseData.instituicoes.filter((i) => i.ativo)
      );
    } catch (err) {
      console.error("Erro ao buscar instituições:", err);
      toast.error("Erro ao carregar instituições");
    }
  };

  const buscarTratamentos = async () => {
    setLoadingTratamentos(true);
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token }
      );
      setTratamentosDisponiveis(
        responseData.tratamentos.filter((t) => t.ativo)
      );
    } catch (err) {
      console.error("Erro ao buscar tratamentos:", err);
      toast.error("Erro ao carregar tratamentos");
    } finally {
      setLoadingTratamentos(false);
    }
  };

  // Lista para o seletor de tratamento: um representante por designação
  // (as variantes por fornecedor são escolhidas no seletor de fornecedor).
  const tratamentosParaSelecao = useMemo(() => {
    const porDesignacao = new Map();
    tratamentosDisponiveis.forEach((t) => {
      if (!porDesignacao.has(t.designacao)) {
        porDesignacao.set(t.designacao, []);
      }
      porDesignacao.get(t.designacao).push(t);
    });
    return Array.from(porDesignacao.values()).map(
      (grupo) => grupo.find((t) => !t.fornecedor) || grupo[0]
    );
  }, [tratamentosDisponiveis]);

  // Tratamento completo (com fornecedor populado) correspondente a um procedimento.
  const getTratamentoAtual = (proc) =>
    tratamentosDisponiveis.find((t) => t._id === proc.tratamento) ||
    proc.tratamentoObj ||
    null;

  // Variantes (por fornecedor) que partilham a mesma designação.
  const getVariantesFornecedor = (proc) => {
    const atual = getTratamentoAtual(proc);
    if (!atual) return [];
    return tratamentosDisponiveis.filter(
      (t) => t.designacao === atual.designacao
    );
  };

  const dentesChangeHandler = (procId, novosDentes) => {
    setProcedimentos(
      procedimentos.map((p) => {
        if (p.id === procId) {
          let facesAtuais = [...p.faces];

          // Desabilita faces se for Arcada
          if (
            novosDentes.includes("Arcada Inferior") ||
            novosDentes.includes("Arcada Superior") ||
            novosDentes.includes("Arcadas")
          ) {
            facesAtuais = [];

            // Se selecionou Arcadas, só mantém Arcadas
            if (novosDentes.includes("Arcadas")) {
              return { ...p, dentes: ["Arcadas"], faces: [] };
            }

            // Se selecionou Arcada Superior/Inferior, filtra apenas essas
            const apenasArcadas = novosDentes.filter(
              (d) => d === "Arcada Inferior" || d === "Arcada Superior"
            );
            return { ...p, dentes: apenasArcadas, faces: [] };
          }

          return { ...p, dentes: novosDentes, faces: facesAtuais };
        }
        return p;
      })
    );
  };

  const adicionarProcedimento = () => {
    const novoId = new Date().getTime().toString();

    // ✅ Adicionar no INÍCIO da lista (não no final)
    setProcedimentos([
      {
        id: novoId,
        tratamento: "",
        tratamentoObj: null,
        dentes: [],
        faces: [],
        listaDentes: DENTES,
        executado: false,
      },
      ...procedimentos, // ✅ procedimentos existentes vêm depois
    ]);

    // ✅ Marcar como recém-adicionado
    setNovoIdProcedimento(novoId);

    // ✅ Remover o destaque após 2 segundos
    setTimeout(() => {
      setNovoIdProcedimento(null);
    }, 2000);
  };

  const removerProcedimento = (id) => {
    const proc = procedimentos.find((p) => p.id === id);

    // Não permitir remover procedimentos executados
    if (proc?.executado) {
      toast.error("Não é possível remover um procedimento já executado");
      return;
    }

    setProcedimentos(procedimentos.filter((p) => p.id !== id));
  };

  const atualizarProcedimento = (id, campo, valor) => {
    setProcedimentos(
      procedimentos.map((p) => {
        if (p.id === id) {
          if (campo === "tratamento") {
            const tratamentoObj = tratamentosDisponiveis.find(
              (t) => t._id === valor
            );

            // Determinar lista de dentes disponíveis
            let listaDentes = DENTES;
            let dentesAtuais = [...p.dentes];

            if (tratamentoObj?.dentesEspecificos) {
              const dentesEspecificosArray = tratamentoObj.dentesEspecificos
                .split(",")
                .map((d) => d.trim());
              listaDentes = dentesEspecificosArray;

              // Se tem apenas 1 dente específico, auto-seleciona
              if (dentesEspecificosArray.length === 1) {
                dentesAtuais = dentesEspecificosArray;
              } else {
                // Filtra dentes atuais para manter apenas os válidos
                dentesAtuais = dentesAtuais.filter((d) =>
                  dentesEspecificosArray.includes(d)
                );
              }
            }

            // Desabilita faces se for Arcada ou se tratamento não aceita faces
            let facesAtuais = [...p.faces];
            if (
              dentesAtuais.includes("Arcada Inferior") ||
              dentesAtuais.includes("Arcada Superior") ||
              dentesAtuais.includes("Arcadas") ||
              !tratamentoObj?.aceitaFaces
            ) {
              facesAtuais = [];
            }

            return {
              ...p,
              tratamento: valor,
              tratamentoObj,
              listaDentes,
              dentes: dentesAtuais,
              faces: facesAtuais,
            };
          }
          return { ...p, [campo]: valor };
        }
        return p;
      })
    );
  };

  const validarProcedimentos = async () => {
    if (procedimentos.length === 0) {
      toast.error("Adicione pelo menos um procedimento");
      return false;
    }

    // Verificar se todos têm tratamento selecionado
    const invalidos = procedimentos.filter((p) => !p.tratamento);
    if (invalidos.length > 0) {
      toast.error("Todos os procedimentos devem ter um tratamento selecionado");
      return false;
    }

    // Validar multiDentes e qtdDentes
    for (const proc of procedimentos) {
      if (proc.tratamentoObj?.aceitaDentes && proc.dentes.length === 0) {
        toast.error(
          `Selecione pelo menos um dente para: ${proc.tratamentoObj.designacao}`
        );
        return false;
      }

      if (
        proc.tratamentoObj?.multiDentes &&
        proc.dentes.length !== proc.tratamentoObj.qtdDentes
      ) {
        toast.error(
          `O tratamento "${proc.tratamentoObj.designacao}" exige exatamente ${proc.tratamentoObj.qtdDentes} dentes. Você selecionou ${proc.dentes.length}.`
        );
        return false;
      }
    }
    // Se orçamento já foi aprovado, validar com backend
    if (orcamento.transformado) {
      try {
        // Processar procedimentos para o formato do backend (mesmo código da função buscarPreview)
        const procedimentosProcessados = [];
        procedimentos.forEach((proc) => {
          if (!proc.dentes || proc.dentes.length === 0) {
            procedimentosProcessados.push({
              tratamento: proc.tratamento,
              dente: "",
              faces: proc.faces || [],
            });
          } else if (proc.dentes.length === 1) {
            procedimentosProcessados.push({
              tratamento: proc.tratamento,
              dente: proc.dentes[0],
              faces: proc.faces || [],
            });
          } else if (proc.dentes.length > 1) {
            if (proc.tratamentoObj?.multiDentes) {
              procedimentosProcessados.push({
                tratamento: proc.tratamento,
                dente: proc.dentes.join(", "),
                faces: proc.faces || [],
              });
            } else {
              proc.dentes.forEach((dente) => {
                procedimentosProcessados.push({
                  tratamento: proc.tratamento,
                  dente: dente,
                  faces: proc.faces || [],
                });
              });
            }
          }
        });

        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/validarprocedimentosubstituicao/${orcamento._id}`,
          "POST",
          JSON.stringify({
            procedimentosNovos: procedimentosProcessados,
          }),
          {
            "Content-Type": "application/json",
            Authorization: "Bearer " + auth.token,
          }
        );

        if (!responseData.valido) {
          setAlertasValidacao(responseData.alertas);
          return false;
        }
      } catch (err) {
        toast.error("Erro ao validar procedimentos");
        return false;
      }
    }

    return true;
  };

  const buscarPreview = async () => {
    setLoadingPreview(true);
    try {
      const endpoint = `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/substituir/${orcamento._id}/preview`;

      // Processar procedimentos para o formato do backend
      const procedimentosProcessados = [];
      procedimentos.forEach((proc) => {
        if (proc.dentes.length === 0) {
          // Tratamento sem dente específico
          procedimentosProcessados.push({
            tratamento: proc.tratamento,
            dente: "",
            faces: proc.faces,
          });
        } else if (proc.dentes.length === 1) {
          // Um único dente
          procedimentosProcessados.push({
            tratamento: proc.tratamento,
            dente: proc.dentes[0],
            faces: proc.faces,
          });
        } else if (proc.dentes.length > 1) {
          // Múltiplos dentes
          if (proc.tratamentoObj?.multiDentes) {
            // Se aceita multiDentes, envia todos juntos separados por vírgula
            procedimentosProcessados.push({
              tratamento: proc.tratamento,
              dente: proc.dentes.join(", "),
              faces: proc.faces,
            });
          } else {
            // Se não aceita multiDentes, cria um procedimento para cada dente
            proc.dentes.forEach((dente) => {
              procedimentosProcessados.push({
                tratamento: proc.tratamento,
                dente: dente,
                faces: proc.faces,
              });
            });
          }
        }
      });

      const body = {
        manterTipo,
        novoTipoCobertura: manterTipo ? null : novoTipoCobertura,
        instituicaoCobertura: manterTipo ? null : instituicaoSelecionada,
        modalidadePagamento: manterTipo
          ? orcamento.modalidadePagamento
          : modalidadePagamento,
        procedimentosNovos: procedimentosProcessados,
        desconto,
        tipoDesconto,
        majoracao,
        comMajoracao,
      };

      const responseData = await sendRequest(
        endpoint,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      setPreviewDados(responseData);
    } catch (err) {
      console.error("Erro ao buscar preview:", err);
      toast.error(err.message || "Erro ao calcular preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validar motivo
      if (!motivoSelecionado) {
        toast.error("Selecione o motivo da substituição");
        return;
      }

      if (
        motivoSelecionado === "OUTRO" &&
        (!motivoOutroTexto || motivoOutroTexto.trim() === "")
      ) {
        toast.error("Descreva o motivo da substituição");
        return;
      }

      if (!manterTipo) {
        if (!novoTipoCobertura) {
          toast.error("Selecione o novo tipo de orçamento");
          return;
        }

        if (novoTipoCobertura === "SEGURADO") {
          if (!instituicaoSelecionada || !modalidadePagamento) {
            toast.error(
              "Preencha todos os campos obrigatórios para orçamento com cobertura"
            );
            return;
          }
        }

        const tipoAtual = orcamento.tipoCobertura; // PARTICULAR, SEGURADO
        const instAtual =
          orcamento.instituicaoCobertura?._id || orcamento.instituicaoCobertura;
        const modalAtual = orcamento.modalidadePagamento;

        if (novoTipoCobertura === "SEGURADO") {
          // Se o orçamento já tinha cobertura (SEGURADO ou MISTO)
          if (tipoAtual !== "PARTICULAR") {
            const mesmaInstituicao = instituicaoSelecionada === instAtual;
            const mesmaModalidade = modalidadePagamento === modalAtual;

            // Se instituição E modalidade são iguais, não há mudança
            if (mesmaInstituicao && mesmaModalidade) {
              toast.error(
                "Altere pelo menos a instituição ou a modalidade de pagamento. O tipo de orçamento permaneceria o mesmo."
              );
              return;
            }
          }
        }
      }

      setActiveStep(1);
    } else if (activeStep === 1) {
      // Validar step 2 (Procedimentos)
      const valido = await validarProcedimentos();
      if (!valido) return;
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Step 3 (Valores) - buscar preview
      await buscarPreview();
      setActiveStep(3);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setAlertasValidacao([]);
  };

  const handleConverter = async () => {
    setLoading(true);
    try {
      const endpoint = `${process.env.REACT_APP_BACKEND_LINK}/api/orcamentos/substituir/${orcamento._id}`;

      // Montar motivo final
      const motivoFinal =
        motivoSelecionado === "OUTRO" ? motivoOutroTexto : motivoSelecionado;

      // Processar procedimentos para o formato do backend (mesmo código da modificação 8)
      const procedimentosProcessados = [];
      procedimentos.forEach((proc) => {
        if (proc.dentes.length === 0) {
          procedimentosProcessados.push({
            tratamento: proc.tratamento,
            dente: "",
            faces: proc.faces,
          });
        } else if (proc.dentes.length === 1) {
          procedimentosProcessados.push({
            tratamento: proc.tratamento,
            dente: proc.dentes[0],
            faces: proc.faces,
          });
        } else if (proc.dentes.length > 1) {
          if (proc.tratamentoObj?.multiDentes) {
            procedimentosProcessados.push({
              tratamento: proc.tratamento,
              dente: proc.dentes.join(", "),
              faces: proc.faces,
            });
          } else {
            proc.dentes.forEach((dente) => {
              procedimentosProcessados.push({
                tratamento: proc.tratamento,
                dente: dente,
                faces: proc.faces,
              });
            });
          }
        }
      });

      const body = {
        manterTipo,
        novoTipoCobertura: manterTipo ? null : novoTipoCobertura,
        instituicaoCobertura: manterTipo ? null : instituicaoSelecionada,
        modalidadePagamento: manterTipo
          ? orcamento.modalidadePagamento
          : modalidadePagamento,
        procedimentosNovos: procedimentosProcessados,
        userId: auth.userId,
        desconto,
        tipoDesconto,
        majoracao,
        comMajoracao,
        observacoes,
        motivoSubstituicao: motivoFinal,
        diasValidadeParcela: 15, // ← ADICIONAR
      };

      const responseData = await sendRequest(
        endpoint,
        "POST",
        JSON.stringify(body),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        }
      );

      toast.success(responseData.mensagem);

      if (responseData.ajustes?.conta) {
        toast.success(responseData.ajustes.conta.mensagem, { duration: 5000 });
      }

      window.location.href = `/clientes/${orcamento.cliente._id}-orcamento`;
      onClose();
    } catch (err) {
      console.error("Erro ao substituir:", err);
      toast.error(err.message || "Erro ao substituir orçamento");
    } finally {
      setLoading(false);
    }
  };

  const calcularMajoracao = () => {
    // Somar todas as comparticipações (valor_cobertura)
    const somaComparticipacoes = procedimentos.reduce((acc, proc) => {
      if (proc.tratamentoObj?.precosPorInstituicao) {
        // Determinar instituição
        const instId = manterTipo
          ? orcamento.instituicaoCobertura?._id ||
            orcamento.instituicaoCobertura
          : instituicaoSelecionada;

        const instIdStr = instId.toString();

        const cobertura = proc.tratamentoObj.precosPorInstituicao.find(
          (p) => (p.instituicao._id || p.instituicao).toString() === instIdStr
        );

        if (cobertura && cobertura.coberto) {
          const qtdDentes = proc.tratamentoObj?.multiDentes
            ? 1 // multiDentes conta como 1 procedimento
            : Math.max(proc.dentes?.length || 1, 1); // senão, multiplica pelo nº de dentes

          return acc + (cobertura.comparticipacao || 0) * qtdDentes;
        }
      }
      return acc;
    }, 0);

    // 15% arredondado por excesso
    const majoracaoCalculada = Math.ceil(somaComparticipacoes * 0.15);

    setMajoracao(majoracaoCalculada);
    setComMajoracao(true);

    toast.success(`Majoração calculada: ${majoracaoCalculada}$00`);
  };
  // RENDERS POR STEP

  const renderStep1 = () => (
    <Box>
      {/* O que estava aqui — "Substitua este orçamento mantendo ou alterando
          seu tipo de cobertura" — descrevia o ecrã, não a operação. O que não
          se percebia é que nasce um orçamento NOVO e o atual é arquivado. */}
      <NotaInformativa
        titulo="O que é substituir um orçamento"
        style={{ margin: "0 0 20px" }}
      >
        <ul style={{ margin: 0, paddingLeft: "18px" }}>
          <li>
            É criado um <strong>orçamento novo</strong>, com um código próprio.
            O atual não desaparece: fica <strong>arquivado</strong> como
            substituído, ligado ao novo e com o motivo que indicar aqui.
          </li>
          <li>
            As marcações que estavam ligadas a este orçamento passam
            automaticamente para o novo.
          </li>
          <li>
            Use isto para <strong>mudar a cobertura</strong> (de particular para
            uma instituição, ou o contrário) ou para{" "}
            <strong>acrescentar e retirar tratamentos</strong> a um orçamento
            que já não pode ser editado.
          </li>
        </ul>
      </NotaInformativa>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          Tipo de Orçamento
        </Typography>
        <RadioGroup
          value={manterTipo}
          onChange={(e) => setManterTipo(e.target.value === "true")}
        >
          <FormControlLabel
            value={true}
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body1">
                  Manter tipo atual:{" "}
                  <strong>
                    {orcamento.tipoCobertura === "PARTICULAR"
                      ? "PARTICULAR"
                      : `${orcamento.beneficio}`}
                  </strong>
                </Typography>
                {orcamento.tipoCobertura !== "PARTICULAR" && (
                  <Typography variant="caption" color="text.secondary">
                    Modalidade: {orcamento.modalidadePagamento}
                  </Typography>
                )}
              </Box>
            }
          />
          <FormControlLabel
            value={false}
            control={<Radio />}
            label="Alterar tipo de orçamento"
          />
        </RadioGroup>
      </FormControl>

      {!manterTipo && (
        <Box
          sx={{ pl: 4, borderLeft: "3px solid", borderColor: "primary.main" }}
        >
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Novo Tipo *</InputLabel>
            <Select
              value={novoTipoCobertura}
              label="Novo Tipo *"
              onChange={(e) => {
                setNovoTipoCobertura(e.target.value);
                if (e.target.value === "PARTICULAR") {
                  setInstituicaoSelecionada("");
                  setModalidadePagamento("DIRETO");
                }
              }}
            >
              <MenuItem value="">Selecione</MenuItem>

              {orcamento.tipoCobertura !== "PARTICULAR" && (
                <MenuItem value="PARTICULAR">Particular</MenuItem>
              )}
              <MenuItem value="SEGURADO">Com Cobertura</MenuItem>
            </Select>
          </FormControl>

          {novoTipoCobertura === "SEGURADO" && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Instituição *</InputLabel>
                <Select
                  value={instituicaoSelecionada}
                  label="Instituição *"
                  onChange={(e) => setInstituicaoSelecionada(e.target.value)}
                >
                  <MenuItem value="">Selecione</MenuItem>
                  {instituicoesDisponiveis.map((inst) => (
                    <MenuItem key={inst._id} value={inst._id}>
                      {inst.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Modalidade de Pagamento *</InputLabel>
                <Select
                  value={modalidadePagamento}
                  label="Modalidade de Pagamento *"
                  onChange={(e) => setModalidadePagamento(e.target.value)}
                >
                  <MenuItem value="DIRETO">Pagamento Direto</MenuItem>
                  <MenuItem value="REEMBOLSO_CLIENTE">
                    Reembolso Cliente
                  </MenuItem>
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom fontWeight="bold">
        Motivo da Substituição *
      </Typography>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Selecione o motivo *</InputLabel>
        <Select
          value={motivoSelecionado}
          label="Selecione o motivo *"
          onChange={(e) => {
            setMotivoSelecionado(e.target.value);
            if (e.target.value !== "OUTRO") {
              setMotivoOutroTexto("");
            }
          }}
          error={!motivoSelecionado && activeStep > 0}
        >
          <MenuItem value="">Selecione</MenuItem>
          <MenuItem value="Conversão de tipo de cobertura">
            Conversão de tipo de cobertura
          </MenuItem>
          <MenuItem value="Adição/Remoção de tratamento">
            Adição/Remoção de tratamento
          </MenuItem>
          <MenuItem value="OUTRO">Outro</MenuItem>
        </Select>
      </FormControl>

      {motivoSelecionado === "OUTRO" && (
        <TextField
          fullWidth
          label="Descreva o motivo *"
          multiline
          rows={3}
          value={motivoOutroTexto}
          onChange={(e) => setMotivoOutroTexto(e.target.value)}
          required
          error={motivoOutroTexto.trim() === "" && activeStep > 0}
          helperText={
            motivoOutroTexto.trim() === "" && activeStep > 0
              ? "Campo obrigatório"
              : "Descreva brevemente o motivo da substituição"
          }
        />
      )}

      {motivoSelecionado && motivoSelecionado !== "OUTRO" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Motivo selecionado: <strong>{motivoSelecionado}</strong>
        </Alert>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Procedimentos</Typography>
        <Button
          startIcon={<PlusCircle />}
          onClick={adicionarProcedimento}
          variant="outlined"
          size="small"
        >
          Adicionar
        </Button>
      </Box>

      {/* A régua verde à volta dos executados não se explica sozinha, e é
          exatamente a dúvida que faz a pessoa carregar no caixote do lixo. */}
      <NotaInformativa style={{ margin: "0 0 16px" }}>
        <ul style={{ margin: 0, paddingLeft: "18px" }}>
          <li>
            Os procedimentos com a marca verde <strong>✓ Executado</strong> já
            foram feitos ao paciente. Não podem ser alterados nem removidos —
            têm de constar no orçamento novo.
          </li>
          <li>
            O que acrescentar ou retirar aqui é refletido no{" "}
            <strong>plano de tratamento</strong> e nos valores.
          </li>
          <li>
            Escolhendo vários dentes para o mesmo tratamento, é criada{" "}
            <strong>uma linha por dente</strong> — exceto nos tratamentos que
            por natureza abrangem vários (por exemplo uma prótese).
          </li>
        </ul>
      </NotaInformativa>

      {alertasValidacao.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {alertasValidacao.map((alerta, idx) => (
            <Typography key={idx} variant="body2">
              • {alerta.mensagem}
            </Typography>
          ))}
        </Alert>
      )}

      {loadingTratamentos ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
          {procedimentos.map((proc, index) => (
            <Box
              key={proc.id}
              sx={{
                border: proc.executado
                  ? "2px solid #4caf50"
                  : "1px solid #e0e0e0",
                borderRadius: 1,
                p: 2,
                mb: 2,
                bgcolor: proc.executado ? "#f1f8f4" : "grey.50",
                position: "relative",
                // ✅ ADICIONADO: Efeito visual para novos procedimentos
                ...(proc.id === novoIdProcedimento && {
                  bgcolor: "#fff3e0", // Laranja claro
                  border: "1px solid #ff9800", // Borda laranja
                  animation: "pulse 1.5s ease-in-out 3", // Pulsa 3 vezes
                  boxShadow: "0 0 20px rgba(255, 152, 0, 0.3)",
                }),
                // ✅ Transição suave quando o efeito desaparece
                transition: "all 0.5s ease-in-out",
              }}
            >
              {proc.executado && (
                <Chip
                  label="✓ Executado"
                  size="small"
                  color="success"
                  sx={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    fontWeight: "bold",
                  }}
                />
              )}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Procedimento #{index + 1}
                </Typography>
                {!proc.executado && (
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removerProcedimento(proc.id)}
                  >
                    <Trash2 fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Autocomplete
                options={tratamentosParaSelecao}
                getOptionLabel={(option) => option.designacao || ""}
                isOptionEqualToValue={(option, value) =>
                  option.designacao === value?.designacao
                }
                value={proc.tratamentoObj}
                onChange={(e, newValue) => {
                  atualizarProcedimento(
                    proc.id,
                    "tratamento",
                    newValue?._id || ""
                  );
                }}
                disabled={proc.executado} // ← DESABILITAR SE EXECUTADO
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      proc.executado
                        ? "Tratamento * (Executado)"
                        : "Tratamento *"
                    }
                    fullWidth
                  />
                )}
                sx={{ mb: 2 }}
              />

              {/* Fornecedor (laboratório): seletor quando existem variantes */}
              {(() => {
                const variantes = getVariantesFornecedor(proc);
                const tratAtual = getTratamentoAtual(proc);
                if (!tratAtual) return null;

                if (variantes.length > 1) {
                  return (
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Fornecedor</InputLabel>
                      <Select
                        value={proc.tratamento}
                        label="Fornecedor"
                        disabled={proc.executado}
                        onChange={(e) =>
                          atualizarProcedimento(
                            proc.id,
                            "tratamento",
                            e.target.value
                          )
                        }
                      >
                        {variantes.map((v) => (
                          <MenuItem key={v._id} value={v._id}>
                            {v.fornecedor?.nome || "Sem fornecedor"}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }

                if (tratAtual.fornecedor?.nome) {
                  return (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      Fornecedor: {tratAtual.fornecedor.nome}
                    </Typography>
                  );
                }
                return null;
              })()}

              {proc.tratamentoObj?.aceitaDentes && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>
                    Dentes
                    {proc.dentes.length > 0 ? ` (${proc.dentes.length})` : ""}
                    {proc.executado && " (Executado)"}
                  </InputLabel>
                  <Select
                    multiple
                    value={proc.dentes}
                    onChange={(e) =>
                      dentesChangeHandler(proc.id, e.target.value)
                    }
                    disabled={proc.executado}
                    input={<OutlinedInput label="Dentes" />}
                    renderValue={(selected) => selected.join(", ")}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 48 * 4.5 + 8,
                          width: 250,
                        },
                      },
                    }}
                  >
                    {proc.listaDentes.map((dente) => (
                      <MenuItem key={dente} value={dente}>
                        <Checkbox checked={proc.dentes.indexOf(dente) > -1} />
                        <ListItemText primary={dente} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {proc.tratamentoObj?.aceitaFaces && (
                <Autocomplete
                  multiple
                  options={FACES_OPCOES}
                  value={proc.faces}
                  onChange={(e, newValue) => {
                    atualizarProcedimento(proc.id, "faces", newValue);
                  }}
                  disabled={proc.executado} // ← DESABILITAR SE EXECUTADO
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={option}
                        {...getTagProps({ index })}
                        size="small"
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Faces" />
                  )}
                />
              )}
            </Box>
          ))}

          {procedimentos.length === 0 && (
            <Alert severity="warning">
              Nenhum procedimento adicionado. Clique em "Adicionar" para
              começar.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );

  const renderStep3 = () => {
    const tipoFinalPreview = manterTipo
      ? orcamento.tipoCobertura
      : novoTipoCobertura;

    // Determinar modalidade final
    const modalidadeFinal = manterTipo
      ? orcamento.modalidadePagamento
      : modalidadePagamento;

    const mostrarMajoracao =
      tipoFinalPreview !== "PARTICULAR" &&
      modalidadeFinal !== "REEMBOLSO_CLIENTE";

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Valores e Descontos
        </Typography>

        {/* Os campos abrem já preenchidos com os valores do orçamento atual, o
            que facilmente passa por "isto é só informativo". Não é: é o que vai
            ficar no orçamento novo. */}
        <NotaInformativa style={{ margin: "0 0 18px" }}>
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li>
              Estes valores vêm do orçamento atual, mas são os que ficam no{" "}
              <strong>orçamento novo</strong>. O que alterar aqui é o que o
              paciente vai pagar.
            </li>
            <li>
              O desconto pode ser em <strong>escudos</strong> ($00) ou em{" "}
              <strong>percentagem</strong> do subtotal.
            </li>
            {mostrarMajoracao && (
              <li>
                A <strong>majoração</strong> é a parte que a clínica acrescenta
                sobre a comparticipação da instituição, e é{" "}
                <strong>subtraída do total</strong> a pagar pelo paciente.{" "}
                <em>Calcular automaticamente</em> aplica os 15% habituais sobre
                o que está coberto.
              </li>
            )}
          </ul>
        </NotaInformativa>

        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <FormControl sx={{ width: "30%" }}>
            <InputLabel>Tipo Desconto</InputLabel>
            <Select
              value={tipoDesconto}
              label="Tipo Desconto"
              onChange={(e) => setTipoDesconto(e.target.value)}
            >
              <MenuItem value="$">$00</MenuItem>
              <MenuItem value="%">%</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Desconto"
            type="number"
            value={desconto}
            onChange={(e) => setDesconto(Number(e.target.value))}
            inputProps={{
              min: 0,
              max: tipoDesconto === "%" ? 100 : undefined,
            }}
          />
        </Box>

        {mostrarMajoracao && (
          <>
            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
              Majoração (15% da comparticipação)
            </Typography>

            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={comMajoracao}
                    onChange={(e) => {
                      setComMajoracao(e.target.checked);
                      if (!e.target.checked) {
                        setMajoracao(0);
                      }
                    }}
                  />
                }
                label="Aplicar majoração"
              />

              {comMajoracao && (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={calcularMajoracao}
                  >
                    Calcular Automaticamente
                  </Button>

                  <TextField
                    label="Majoração (manual)"
                    type="number"
                    value={majoracao}
                    onChange={(e) => setMajoracao(Number(e.target.value))}
                    inputProps={{ min: 0 }}
                    sx={{ width: "200px" }}
                  />
                </>
              )}
            </Box>

            {comMajoracao && majoracao > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Majoração de <strong>{majoracao}$00</strong> será subtraida do
                total
              </Alert>
            )}
          </>
        )}

        <Divider sx={{ my: 2 }} />

        <TextField
          fullWidth
          label="Observações"
          multiline
          rows={3}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
        />
      </Box>
    );
  };

  const renderStep4 = () => {
    // Identificar procedimentos adicionados e removidos
    const identificarMudancasProcedimentos = () => {
      const procsOriginais = orcamento.procedimentos.map((proc) => ({
        tratamento: proc.tratamento?._id || proc.tratamento,
        dente: proc.dente || "",
        faces: (proc.faces || []).slice().sort().join(","),
        designacao:
          typeof proc.tratamento === "object"
            ? proc.tratamento.designacao
            : tratamentosDisponiveis.find(
                (t) => t._id === (proc.tratamento?._id || proc.tratamento)
              )?.designacao || "Tratamento",
      }));

      const procsAtuais = [];
      procedimentos.forEach((proc) => {
        if (!proc.dentes || proc.dentes.length === 0) {
          procsAtuais.push({
            tratamento: proc.tratamento,
            dente: "",
            faces: (proc.faces || []).slice().sort().join(","),
            designacao: proc.tratamentoObj?.designacao || "Tratamento",
          });
        } else if (proc.dentes.length === 1) {
          procsAtuais.push({
            tratamento: proc.tratamento,
            dente: proc.dentes[0],
            faces: (proc.faces || []).slice().sort().join(","),
            designacao: proc.tratamentoObj?.designacao || "Tratamento",
          });
        } else if (proc.dentes.length > 1) {
          if (proc.tratamentoObj?.multiDentes) {
            procsAtuais.push({
              tratamento: proc.tratamento,
              dente: proc.dentes.join(", "),
              faces: (proc.faces || []).slice().sort().join(","),
              designacao: proc.tratamentoObj?.designacao || "Tratamento",
            });
          } else {
            proc.dentes.forEach((dente) => {
              procsAtuais.push({
                tratamento: proc.tratamento,
                dente: dente,
                faces: (proc.faces || []).slice().sort().join(","),
                designacao: proc.tratamentoObj?.designacao || "Tratamento",
              });
            });
          }
        }
      });

      // Encontrar removidos
      const removidos = procsOriginais.filter(
        (orig) =>
          !procsAtuais.some(
            (atual) =>
              atual.tratamento === orig.tratamento &&
              atual.dente === orig.dente &&
              atual.faces === orig.faces
          )
      );

      // Encontrar adicionados
      const adicionados = procsAtuais.filter(
        (atual) =>
          !procsOriginais.some(
            (orig) =>
              orig.tratamento === atual.tratamento &&
              orig.dente === atual.dente &&
              orig.faces === atual.faces
          )
      );

      return { removidos, adicionados };
    };

    const { removidos, adicionados } = identificarMudancasProcedimentos();

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Resumo da Substituição
        </Typography>

        {/* Último ecrã antes de a coisa acontecer. É aqui que vale a pena dizer
            o que muda na conta corrente, porque é o que ninguém espera. */}
        <NotaInformativa
          titulo="Antes de confirmar"
          style={{ margin: "0 0 18px" }}
        >
          <ul style={{ margin: 0, paddingLeft: "18px" }}>
            <li>
              Confira as linhas a vermelho (retiradas) e a verde
              (acrescentadas), e o <strong>Novo Total</strong>.
            </li>
            <li>
              {orcamento.transformado ? (
                <>
                  Este orçamento <strong>já foi aprovado</strong>: a conta
                  corrente e o plano de tratamento passam para o orçamento novo
                  e são acertados. Se o total subir, o valor em falta aparece
                  numa <strong>parcela nova</strong>; o que já foi pago
                  mantém-se.
                </>
              ) : (
                <>
                  Como este orçamento ainda <strong>não foi aprovado</strong>, a
                  conta atual é desativada e é criada a conta do orçamento novo.
                  Nada do que já foi pago é afetado.
                </>
              )}
            </li>
            <li>
              As marcações passam para o orçamento novo, mas as que estavam
              ligadas a tratamentos <strong>retirados</strong> têm de ser
              corrigidas por si, na agenda.
            </li>
          </ul>
        </NotaInformativa>

        {previewDados?.mudancaTipo && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> O tipo de cobertura será alterado de{" "}
              <strong>{previewDados.tipoAntigo}</strong> para{" "}
              <strong>{previewDados.tipoNovo}</strong>
            </Typography>
          </Alert>
        )}

        {/* Seção de Mudanças de Procedimentos */}
        {(removidos.length > 0 || adicionados.length > 0) && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              Alterações nos Procedimentos
            </Typography>

            {removidos.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Procedimentos Removidos ({removidos.length}):
                </Typography>
                {removidos.map((proc, idx) => (
                  <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                    • {proc.designacao}
                    {proc.dente && ` - Dente: ${proc.dente}`}
                    {proc.faces &&
                      ` - Faces: ${proc.faces.replace(/,/g, ", ")}`}
                  </Typography>
                ))}
              </Alert>
            )}

            {adicionados.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Procedimentos Adicionados ({adicionados.length}):
                </Typography>
                {adicionados.map((proc, idx) => (
                  <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                    • {proc.designacao}
                    {proc.dente && ` - Dente: ${proc.dente}`}
                    {proc.faces &&
                      ` - Faces: ${proc.faces.replace(/,/g, ", ")}`}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        )}

        {loadingPreview ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : previewDados ? (
          <>
            {previewDados.erro && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewDados.mensagem}
              </Alert>
            )}

            {!previewDados.erro && (
              <>
                {previewDados.tratamentosNaoCobertos > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Este orçamento ficará MISTO:{" "}
                    {previewDados.tratamentosCobertos} tratamento(s) coberto(s)
                    e {previewDados.tratamentosNaoCobertos} tratamento(s)
                    particular(es).
                  </Alert>
                )}
                <Box
                  sx={{
                    bgcolor: "grey.50",
                    p: 2,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Subtotal Atual:
                    </Typography>
                    <Typography variant="body2">
                      {Intl.NumberFormat("pt-BR").format(orcamento.subtotal)}$00
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Novo Subtotal:
                    </Typography>
                    <Typography
                      variant="body2"
                      color={
                        previewDados.novoSubtotal > orcamento.subtotal
                          ? "error.main"
                          : "success.main"
                      }
                    >
                      {Intl.NumberFormat("pt-BR").format(
                        previewDados.novoSubtotal
                      )}
                      $00
                    </Typography>
                  </Box>

                  {previewDados.valorCobertura > 0 &&
                    previewDados.subtotalCliente !==
                      previewDados.novoSubtotal && (
                      <>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Total Reembolso/Cobertura:
                          </Typography>
                          <Typography variant="body2" color="info.main">
                            {Intl.NumberFormat("pt-BR").format(
                              previewDados.valorCobertura
                            )}
                            $00
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Subtotal Cliente:
                          </Typography>
                          <Typography variant="body2">
                            {Intl.NumberFormat("pt-BR").format(
                              previewDados.subtotalCliente
                            )}
                            $00
                          </Typography>
                        </Box>
                      </>
                    )}

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Desconto:
                    </Typography>
                    <Typography variant="body2">
                      {Intl.NumberFormat("pt-BR").format(previewDados.desconto)}
                      $00
                    </Typography>
                  </Box>

                  {previewDados.majoracao > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Majoração:
                      </Typography>
                      <Typography variant="body2" color="warning.main">
                        -
                        {Intl.NumberFormat("pt-BR").format(
                          previewDados.majoracao
                        )}
                        $00
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Total Atual:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {Intl.NumberFormat("pt-BR").format(orcamento.total)}$00
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body1" fontWeight="bold">
                      Novo Total:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color={
                        previewDados.novoTotal > orcamento.total
                          ? "error.main"
                          : "success.main"
                      }
                    >
                      {Intl.NumberFormat("pt-BR").format(
                        previewDados.novoTotal
                      )}
                      $00
                    </Typography>
                  </Box>
                </Box>
                {orcamento.transformado && previewDados.ajusteConta && (
                  <Alert
                    severity={
                      previewDados.ajusteConta.tipo === "PARCELA_ADICIONADA"
                        ? "warning"
                        : "info"
                    }
                  >
                    <Typography variant="body2">
                      <strong>Ajuste de Conta Corrente:</strong>{" "}
                      {previewDados.ajusteConta.mensagem}
                    </Typography>
                  </Alert>
                )}

                {previewDados.ajustePlano && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Ajuste de Plano de Tratamento:</strong>
                    </Typography>
                    {previewDados.ajustePlano.adicionados > 0 && (
                      <Typography variant="body2">
                        • {previewDados.ajustePlano.adicionados} procedimento(s)
                        adicionado(s)
                      </Typography>
                    )}
                    {previewDados.ajustePlano.removidos > 0 && (
                      <Typography variant="body2">
                        • {previewDados.ajustePlano.removidos} procedimento(s)
                        removido(s)
                      </Typography>
                    )}
                  </Alert>
                )}
                {previewDados.agendamentosAfetados?.length > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      ⚠️ Atenção: Existem{" "}
                      {previewDados.agendamentosAfetados.length} agendamento(s)
                      futuro(s) associado(s) a procedimentos deverão ser
                      removidos ou substituidos. Por favor, faça a devida
                      correção!
                    </Typography>
                    {previewDados.agendamentosAfetados
                      .slice(0, 5)
                      .map((ag, idx) => (
                        <Typography key={idx} variant="body2" sx={{ ml: 2 }}>
                          • {new Date(ag.data).toLocaleDateString("pt-BR")} -{" "}
                          {ag.tratamento}
                          {ag.medico && ` (Dr. ${ag.medico})`}
                        </Typography>
                      ))}
                    {previewDados.agendamentosAfetados.length > 5 && (
                      <Typography
                        variant="body2"
                        sx={{ ml: 2, fontStyle: "italic" }}
                      >
                        ... e mais{" "}
                        {previewDados.agendamentosAfetados.length - 5}{" "}
                        agendamento(s)
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Após a substituição, verifique a agenda do cliente para
                      reagendar ou cancelar.
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </>
        ) : null}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Replace /> Substituir Orçamento
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
        {activeStep === 3 && renderStep4()}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={loading}>
            Voltar
          </Button>
        )}
        {activeStep < 3 ? (
          <Button onClick={handleNext} variant="contained" disabled={loading}>
            Próximo
          </Button>
        ) : (
          <Button
            onClick={handleConverter}
            variant="contained"
            disabled={loading || previewDados?.erro}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? "Substituindo..." : "Confirmar Substituição"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ModalSubstituicaoOrcamento;
